// src/components/HomeHero.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";

import "./homeHero.css";
import WhiteSearchIcon from "./assets/white-search-icon.png";
import iconSearch from "./assets/icon-search.png";
import iconLocation from "./assets/icon-location.png";
import iconPractice from "./assets/icon-practice.png";
import PracticeAreaPicker from "./components/practiceAreaPicker";
import heroIllu from "./assets/searchImage.png";
import CustomCompleteBox from "./components/customCompleteBox";

const DEFAULT_SUGGESTION = {
  title: "United States, IL, 60602, Chicago",
  id: "here:cm:namedplace:21002647",
  address: {
    label: "Chicago, IL, United States",
    countryCode: "USA",
    state: "Illinois",
    stateCode: "IL",
    // postalCode, city, county, etc. only if you need them:
    postalCode: "60602",
    city: "Chicago",
  },
  language: "en",
  localityType: "city",
  resultType: "locality",
  highlights: {
    title: [
      { start: 0, end: 13 },
      { start: 15, end: 17 },
      { start: 19, end: 24 },
      { start: 26, end: 33 },
    ],
  },
};

const DEFAULT_COORDS = {
  latitude: 41.88425,
  longitude: -87.63245,
};

// Default Chicago coords & label

export default function HomeHero() {
  const navigate = useNavigate();

  // Search fields
  const [name, setName] = useState("");
  // location autocomplete
  const [locationInput, setLocationInput] = useState(DEFAULT_SUGGESTION.title);
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(DEFAULT_SUGGESTION);
  const [addressDetails, setAddressDetails] = useState({
    ...DEFAULT_COORDS,
  });
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState("");

  // Practice-area picker
  const [practiceArea, setPracticeArea] = useState({ slug: "", label: "" });
  const [showPicker, setShowPicker] = useState(false);

  // A simple in-memory cache for geocoding lookups
  const geocodeCache = useRef({});

  // Debounced fetch for HERE autocomplete
  const debouncedFetch = useCallback(
    debounce(async (q) => {
      if (q.length < 3) {
        setAutocompleteResults([]);
        return;
      }
      setIsLoadingLocation(true);
      setLocationError("");
      try {
        const res = await fetch(
          `https://autocomplete.search.hereapi.com/v1/autocomplete?${new URLSearchParams(
            {
              q,
              apiKey: process.env.REACT_APP_HERE_API_KEY,
            }
          )}`
        );
        if (!res.ok) throw new Error(res.statusText);
        const payload = await res.json();
        setAutocompleteResults(payload.items || []);
      } catch (e) {
        console.error(e);
        setLocationError("Failed to load suggestions.");
      } finally {
        setIsLoadingLocation(false);
      }
    }, 300),
    []
  );

  // Kick off autocomplete when the input changes
  useEffect(() => {
    debouncedFetch(locationInput);
    return debouncedFetch.cancel;
  }, [locationInput, debouncedFetch]);

  // Geocode a full address string via HERE’s geocode endpoint
  const fetchGeocode = async (address) => {
    if (geocodeCache.current[address]) {
      return geocodeCache.current[address];
    }
    try {
      const res = await fetch(
        `https://geocode.search.hereapi.com/v1/geocode?${new URLSearchParams({
          q: address,
          apiKey: process.env.REACT_APP_HERE_API_KEY,
        })}`
      );
      if (!res.ok) throw new Error(res.statusText);
      const { items } = await res.json();
      if (items?.length) {
        const { lat, lng } = items[0].position;
        const coords = { latitude: lat, longitude: lng };
        geocodeCache.current[address] = coords;
        return coords;
      }
    } catch (e) {
      console.error(e);
    }
    // fallback to default if geocode fails
    return { ...DEFAULT_COORDS };
  };

  // When user selects an autocomplete suggestion
  const handleSelectLocation = async (suggestion) => {
    setSelectedLocation(suggestion);
    setLocationInput(suggestion.title);
    setAutocompleteResults([]);

    // lookup lat/lng and store
    const coords = await fetchGeocode(suggestion.title);
    setAddressDetails(coords);
  };

  // Final search handler
  const handleSearch = () => {
    const { latitude, longitude } = addressDetails;

    const doNavigate = (lat, lon) => {
      const params = new URLSearchParams({
        name,
        practice_area: practiceArea.slug,
        latitude: lat,
        longitude: lon,
        radius: "40000",
        location: selectedLocation?.title,
      }).toString();
      navigate(`/search-results?${params}`);
    };

    // If user picked from autocomplete, just use those coords
    if (selectedLocation) {
      return doNavigate(latitude, longitude);
    }

    // Otherwise, ask for geolocation
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          doNavigate(pos.coords.latitude, pos.coords.longitude);
        },
        (_) => {
          // denied or error → fallback to default Chicago
          doNavigate(DEFAULT_COORDS.latitude, DEFAULT_COORDS.longitude);
        }
      );
    } else {
      // no navigator.geolocation support
      doNavigate(DEFAULT_COORDS.latitude, DEFAULT_COORDS.longitude);
    }
  };

  return (
    <section className="hero">
      <div className="home__hero__container">
        <div className="home__hero__text">
          <h1 className="home-hero__heading">
            Find your trusted legal expert <br />
            <span>AI-powered legal connections tailored to your needs</span>
          </h1>

          <div className="hero__search-container">
            <div className="hero__search-box">
              {/* Name */}
              <div className="hero__field">
                <img
                  src={iconSearch}
                  alt="Search Icon"
                  className="hero__icon"
                />
                <input
                  type="text"
                  placeholder="Search by name, case type, or firm..."
                  className="hero__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="hero__divider" />

              {/* Location (autocomplete) */}
              <div className="hero__field">
                <img
                  src={iconLocation}
                  alt="Location Icon"
                  className="hero__icon"
                />
                <CustomCompleteBox
                  title=""
                  placeholder="City, state, or zip code"
                  options={autocompleteResults}
                  value={selectedLocation}
                  inputValue={locationInput}
                  onInputChange={(val) => {
                    setLocationInput(val);
                    setSelectedLocation(null);
                  }}
                  onChange={handleSelectLocation}
                  getOptionLabel={(item) => item.title}
                  isLoading={isLoadingLocation}
                  error={locationError}
                />
              </div>
              <div className="hero__divider" />

              {/* Practice Area */}
              <div className="hero__field">
                <img
                  src={iconPractice}
                  alt="Practice Area Icon"
                  className="hero__icon"
                />
                <input
                  type="text"
                  placeholder="Practice area"
                  className="hero__input"
                  value={practiceArea.label}
                  readOnly
                  onClick={() => setShowPicker(true)}
                />
              </div>
            </div>

            {/* Search button */}
            <button className="home__hero__button" onClick={handleSearch}>
              <img
                src={WhiteSearchIcon}
                alt="search-icon"
                style={{ width: 32, height: 32 }}
              />
            </button>

            {/* Illustration */}
            <div className="hero__image">
              <img src={heroIllu} alt="Hero Illustration" />
            </div>
          </div>
        </div>
      </div>

      {/* Practice Area Picker */}
      <PracticeAreaPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(choice) => setPracticeArea(choice)}
      />
    </section>
  );
}
